import { useQuery } from '@tanstack/react-query';
import { MessageSquarePlus, MessagesSquare } from 'lucide-react';

import { chatConversationsKey, listConversations, type ChatConversationListItem } from '@/api/comms/chat.api';
import { cn } from '@/lib/cn';
import { conversationDisplayName, formatRelativeTime } from '@/lib/comms/format';

/**
 * Left rail of the Messages page (Stream E, ADR-027 §7.2). Self-fetching
 * on a 15s poll while mounted; ChatThread's send/read mutations invalidate
 * the same `chatConversationsKey()` so the previews + unread pills stay
 * live. Rows show the conversation name, last-message preview, relative
 * time, and an unread pill, with the active thread highlighted.
 */

interface ConversationListProps {
  activeConversationId: string | null;
  myMembershipId: string | null;
  onSelect: (conversationId: string) => void;
  onNewConversation: () => void;
}

export function ConversationList({
  activeConversationId,
  myMembershipId,
  onSelect,
  onNewConversation,
}: ConversationListProps) {
  const query = useQuery({
    queryKey: chatConversationsKey(),
    queryFn: listConversations,
    refetchInterval: 15_000,
  });

  const conversations = query.data ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-kw-border px-4 py-3">
        <h1 className="typo-h3">Messages</h1>
        <button
          type="button"
          onClick={onNewConversation}
          aria-label="New message"
          className="rounded-kw-md p-1.5 text-kw-cerulean hover:bg-kw-cerulean-light"
        >
          <MessageSquarePlus className="size-5" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {query.isLoading ? (
          <p className="px-4 py-6 text-[13px] text-kw-muted">Loading conversations…</p>
        ) : query.error ? (
          <p className="px-4 py-6 text-[13px] text-kw-danger-text">Could not load conversations.</p>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
            <MessagesSquare className="size-9 text-kw-ghost" />
            <div>
              <p className="text-[14px] font-medium text-kw-shark">No conversations yet</p>
              <p className="mt-1 text-[13px] text-kw-muted">Start a direct message or a group chat.</p>
            </div>
            <button
              type="button"
              onClick={onNewConversation}
              className="text-[13px] font-medium text-kw-cerulean hover:underline"
            >
              New message
            </button>
          </div>
        ) : (
          <ul className="divide-y divide-kw-border">
            {conversations.map((conversation) => (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                myMembershipId={myMembershipId}
                active={conversation.id === activeConversationId}
                onSelect={() => {
                  onSelect(conversation.id);
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function ConversationRow({
  conversation,
  myMembershipId,
  active,
  onSelect,
}: {
  conversation: ChatConversationListItem;
  myMembershipId: string | null;
  active: boolean;
  onSelect: () => void;
}) {
  const name = conversationDisplayName(conversation, myMembershipId);
  const preview = conversation.lastMessage?.bodyText ?? 'No messages yet';
  const previewIsOwn = conversation.lastMessage?.senderMembershipId === myMembershipId;
  const hasUnread = conversation.unreadCount > 0;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={active ? 'true' : undefined}
        className={cn(
          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
          active ? 'bg-kw-cerulean-light' : 'hover:bg-kw-bg',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-[13px]',
                hasUnread ? 'font-semibold text-kw-shark' : 'font-medium text-kw-shark',
              )}
            >
              {name}
            </span>
            {conversation.lastMessage ? (
              <span className="shrink-0 text-[11px] text-kw-muted">
                {formatRelativeTime(conversation.lastMessage.createdAt)}
              </span>
            ) : null}
          </div>
          <p className={cn('mt-0.5 truncate text-[12px]', hasUnread ? 'text-kw-shark' : 'text-kw-muted')}>
            {previewIsOwn && conversation.lastMessage ? 'You: ' : ''}
            {preview}
          </p>
        </div>
        {hasUnread ? (
          <span className="mt-0.5 grid min-w-5 shrink-0 place-items-center rounded-kw-full bg-kw-cerulean px-1.5 py-0.5 text-[11px] font-semibold text-white">
            {conversation.unreadCount > 9 ? '9+' : conversation.unreadCount}
          </span>
        ) : null}
      </button>
    </li>
  );
}
